import { useEffect } from "react";

// Feb-2026 — Deep-Link-Verhalten der CreateListingPage, ausgelagert aus
// CreateListingPage.jsx:
//   • `?focus=disposition|portal` → springt/scrollt zur Sektion, klappt auf,
//     optional `?action=sell` stößt den Verkaufs-Workflow an.
//   • `?sale_type=auction|rental` → belegt Verkaufsart + Zusatzfelder vor.
export default function useListingDeepLinks({ searchParams, isEdit, setForm }) {
    // Feb-2026 (User-Wunsch 26 May): Deep-Link `?focus=disposition` springt direkt
    // zur Disposition-Sektion. Wir warten bis sich die Form initialisiert hat
    // (300 ms reichen), klappen den Section-Toggle auf und scrollen zentral.
    useEffect(() => {
        const focus = searchParams.get("focus");
        if (!focus) return;
        // Jul-2026 (User-Wunsch) — `?focus=archiv` springt direkt zum
        // KI-Dokumentenarchiv des Fahrzeugs (liegt in der zugeklappten
        // Disposition-Sektion → erst aufklappen, dann zum Archiv scrollen).
        if (focus === "archiv") {
            const tryArchiv = () => {
                const sec = document.querySelector('[data-testid="disposition-section-toggle"]');
                if (!sec) return false;
                const toggle = sec.querySelector('[aria-expanded="false"]');
                if (toggle) { try { toggle.click(); } catch { /* noop */ } }
                let tries = 0;
                const scrollArchive = () => {
                    const el = document.querySelector('[data-testid="document-archive-section"]');
                    if (el) {
                        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
                        el.classList.add("ring-2", "ring-[#0EA5E9]", "ring-offset-2");
                        setTimeout(() => el.classList.remove("ring-2", "ring-[#0EA5E9]", "ring-offset-2"), 2500);
                        return;
                    }
                    if (tries++ < 10) setTimeout(scrollArchive, 350);
                };
                setTimeout(scrollArchive, 300);
                return true;
            };
            const ta = setTimeout(() => { if (!tryArchiv()) setTimeout(tryArchiv, 800); }, 300);
            return () => clearTimeout(ta);
        }
        const map = {
            disposition: "disposition-section-toggle",
            portal: "portal-transaction-section",
        };
        const testid = map[focus];
        if (!testid) return;
        const tryScroll = () => {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (!el) return false;
            const toggle = el.querySelector('[aria-expanded="false"]');
            if (toggle) { try { toggle.click(); } catch { /* noop */ } }
            try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* noop */ }
            el.classList.add("ring-2", "ring-[#0EA5E9]", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#0EA5E9]", "ring-offset-2"), 2000);
            // Feb-2026 (User-Wunsch) — Deep-Link `?action=sell|auction|rental` stößt
            // nach dem Aufklappen direkt den passenden Workflow in der Disposition
            // an (Proxy-Buttons in DispositionActions). Mehrfach versuchen, bis die
            // Disposition gemountet ist.
            const actionMap = {
                sell: "dispo-btn-primary-sell",
                auction: "dispo-btn-quick-auction",
                rental: "dispo-btn-quick-rental",
            };
            const actionTestId = actionMap[searchParams.get("action")];
            if (actionTestId) {
                let tries = 0;
                const fire = () => {
                    const actBtn = document.querySelector(`[data-testid="${actionTestId}"]`);
                    if (actBtn) { try { actBtn.click(); } catch { /* noop */ } return; }
                    if (tries++ < 8) setTimeout(fire, 350);
                };
                setTimeout(fire, 500);
            }
            return true;
        };
        const t1 = setTimeout(() => { if (!tryScroll()) setTimeout(tryScroll, 800); }, 300);
        return () => clearTimeout(t1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("focus")]);

    // Feb-2026 (User-Wunsch) — Deep-Link `?sale_type=auction|rental` aus den
    // Dashboard-Schnellbuttons „Fahrzeug zur Auktion" / „Fahrzeug zur Vermietung".
    // Die Basis-Fahrzeugdaten werden ganz normal erfasst; vorbelegt wird nur die
    // Verkaufsart + die jeweiligen Zusatzfelder (Auktion bzw. Vermietung).
    useEffect(() => {
        if (isEdit) return; // im Edit-Modus den bestehenden Verkaufstyp nicht überschreiben
        const st = searchParams.get("sale_type");
        if (st !== "auction" && st !== "rental") return;
        setForm((p) => {
            if (st === "auction") {
                const d = new Date();
                d.setDate(d.getDate() + 7);
                d.setSeconds(0, 0);
                return {
                    ...p,
                    sale_type: "auction",
                    auction_end: p.auction_end || d.toISOString().slice(0, 16),
                    auction_audience: p.auction_audience || "b2b_only",
                };
            }
            return { ...p, sale_type: "rental", sharing_enabled: true };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("sale_type")]);

    // Jun-2026 (User-Wunsch) — Deep-Link `?mode=bestellung` (EU-Fahrzeugbestellung,
    // „Bestellfahrzeug anlegen"): Zustand „Neufahrzeug" + Lager-Status „Bestellung"
    // (Erst auf Bestellung) vorbelegen. Erstzulassung/KM/Vorbesitzer entfallen.
    useEffect(() => {
        if (isEdit) return;
        if (searchParams.get("mode") !== "bestellung") return;
        setForm((p) => ({
            ...p,
            condition: "Neufahrzeug",
            stock_state: "bestell",
            stock_count: 1,
            vat_included: true,
            mileage: 0,
            previous_owners: 0,
            first_registration_date: "",
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("mode")]);
}
